// useHistory.ts
import { useState, useEffect } from 'react';
import { HistoryAbout } from '../Types/Types';

const useHistory = () => {
  const [history, setHistory] = useState<HistoryAbout[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('position');
  const [sortOrder, setSortOrder] = useState<string>("asc");

  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api/history`;

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const lang = localStorage.getItem('lang') || 'en';
    const encodedSearchQuery = encodeURIComponent(searchQuery);
    const fullUrl = `${url}?lang=${lang}&search=${encodedSearchQuery}&sortBy=position&sortOrder=asc`;



    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch history');
      }

      const data: HistoryAbout[] = await response.json();
      setHistory(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching history');
    } finally {
      setLoading(false);
    }
  };

  const createHistory = async (text: { en: string; mk: string; sq?: string }) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create history');
      }

      if (!response.ok) {
        throw new Error("Failed to create About Us entry.");
      }

      await fetchHistory();
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating history');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateHistory = async (id: string, formData: object) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update history");
      }
      await fetchHistory();
      return true;
    } catch (err: any) {
      setError(err.message || "An error occurred");
      return false; // This was missing!
    } finally {
      setLoading(false);
    }
  };

  const deleteHistory = async (id: string) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${url}/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete history');
      }

      setHistory(prev => prev.filter(item => item._id !== id));
      await fetchHistory();
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting history');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reorderHistory = async (newOrder: { _id: string; position: number }[]) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${url}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(newOrder),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reorder history');
      }

      // Update local state to reflect the new order
      setHistory(prevHistory => {
        // Sort local history by the new positions from newOrder
        const orderMap = new Map(newOrder.map(item => [item._id, item.position]));
        return [...prevHistory].sort((a, b) => (orderMap.get(a._id) ?? 0) - (orderMap.get(b._id) ?? 0));
      });
      await fetchHistory();
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while reordering');
      return false;
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchHistory();
  }, [searchQuery, sortBy, sortOrder]);

  return {
    history,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    deleteHistory,
    createHistory,
    updateHistory,
    fetchHistory,
    reorderHistory
  };
};

export default useHistory;